import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalRole,
  ControlSubrole,
  NULL_SUBROLE_KEY,
} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {getRoleSymbol} from 'samples/experimental-roles/ExperimentalRoleUtil';
import PrimaryExperimentalRoleSymbol from 'samples/experimental-roles/PrimaryExperimentalRoleSymbol';
import WellRoleGroupButtons from 'samples/plate-maps/annotate-plate-modal/WellRoleGroupButtons';
import {BLUE_LIGHTER, BLUE_POWDER_LIGHT, GRAY} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import {capitalize} from 'util/NameUtil';

// Control wells are not required to have a subrole, so we include a null option
const CONTROL_SUBROLE_OPTIONS = [
  {primaryRole: PrimaryExperimentalRoleType.CONTROL, subrole: null},
  ...Object.keys(ControlSubrole).map((subrole: ControlSubrole) => ({
    primaryRole: PrimaryExperimentalRoleType.CONTROL,
    subrole,
  })),
];

interface ControlSubroleButtonsWithoutGroupsProps {
  groupPopoverDisabled: true;
  selectedSubrole: string | null;
  setSelectedSubrole: (subrole: string | null) => void;
}

interface ControlSubroleButtonsWithGroupsProps {
  groupPopoverDisabled: false;
  onAssignGroup: (role: ExperimentalRole) => void;
  groupsBySubrole: {[subrole: string]: Array<ExperimentalRole>};
}

type ControlSubroleButtonsProps =
  | ControlSubroleButtonsWithoutGroupsProps
  | ControlSubroleButtonsWithGroupsProps;

/**
 * List of buttons for assigning control well roles with an optional subrole and group.
 * If groupPopoverDisabled is false, a popover to assign a specific control role with a group number appears.
 * If groupPopoverDisabled is true, there is only an option to select subroles and no group options.
 */
export default function ControlSubroleButtons(props: ControlSubroleButtonsProps): JSX.Element {
  function renderPopoverContent(subrole: string | null): ReactNode {
    if (!props.groupPopoverDisabled) {
      return (
        <div className={css(styles.subrolePopover)}>
          <WellRoleGroupButtons
            primaryRole={PrimaryExperimentalRoleType.CONTROL}
            subrole={subrole}
            onAssignGroup={props.onAssignGroup}
            roleGroups={props.groupsBySubrole[subrole ?? NULL_SUBROLE_KEY] ?? []}
          />
        </div>
      );
    }
    return null;
  }

  return (
    <div className={css(styles.groupsContainer)}>
      {CONTROL_SUBROLE_OPTIONS.map((groupOption) => (
        <Popover
          key={groupOption.subrole}
          interaction={FloatingInteraction.CLICK}
          placement={FloatingPlacement.BOTTOM_START}
          renderContent={() => renderPopoverContent(groupOption.subrole)}
          isDisabled={props.groupPopoverDisabled}
        >
          <span
            className={classNames(css(styles.roleOption), css(styles.groupOption), {
              [css(styles.selectedOption)]:
                props.groupPopoverDisabled && props.selectedSubrole === groupOption.subrole,
            })}
            onClick={() => {
              if (props.groupPopoverDisabled) {
                props.setSelectedSubrole(groupOption.subrole);
              }
            }}
          >
            <PrimaryExperimentalRoleSymbol
              radius={10}
              primaryRole={groupOption.primaryRole}
              label={getRoleSymbol(groupOption)}
            />
            <p className={css(styles.subroleOptionText)}>{capitalize(groupOption.subrole ?? 'Control')}</p>
          </span>
        </Popover>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  roleOption: {
    borderRadius: 4,
    cursor: 'pointer',
    textAlign: 'center',
    border: `1px solid ${GRAY}`,
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
    },
  },
  selectedOption: {
    borderColor: BLUE_LIGHTER,
  },
  subrolePopover: {
    width: 200,
  },
  subroleOptionText: {
    margin: 'auto 2px auto 4px',
  },
  groupsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  groupOption: {
    display: 'flex',
    height: 28,
    padding: 3,
    margin: '4px 8px 4px 0',
  },
});
